import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { FaUserSecret } from 'react-icons/fa';
import { functions } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';
import Button from './Button';
import { useToast } from './Toast';
import '../../assets/styles/scss/components/InternalIpBanner.scss';

// "Not now" is remembered for this browser session, whatever network it is on. (Until Oct 2026
// it was remembered per network, under a key that held the address. The page no longer has an
// address to key it on, and should not.)
const DISMISSED_KEY = 'cz_team_network_dismissed';

/**
 * useInternalIpStatus
 *
 * Is the network this colleague is on already on the team's list, so that their own testing
 * is left out of Engagement? Shared by InternalIpBadge (it is: quiet reassurance under the
 * page header) and InternalIpAskBanner (it is not: a full-width ask at the top of the page),
 * so the question is asked once.
 *
 * ⚠️ THE PAGE IS TOLD ONE FACT AND HOLDS NO ADDRESS (step e of the team-networks move, Oct
 * 2026; AGENTS.md). Until then it fetched its own address AND the whole list (every network's
 * address, its label, and the id and email of whoever added it), compared them here, and sent
 * the address back to be added: so any colleague's browser held the list, and the function
 * that added a network took whatever address it was sent. Now:
 *   · `myNetworkStatus` answers `{ known }` and nothing else;
 *   · `addMyNetwork` is sent NOTHING and adds the network the call came from, as the server
 *     saw it (functions/teamNetworks.js, callersAddress).
 * Which networks count as the same (for IPv6, the same /64) is decided on the server only.
 *
 * Nothing is stored unless the colleague clicks Yes.
 *
 * @returns {{status: 'none'|'known'|'unknown'|'dismissed'|'cannot-tell', adding: boolean, handleAdd: Function, handleDismiss: Function}}
 *   status:
 *     'none'        — not signed in, or the question has not been answered (render nothing)
 *     'known'       — this network is already on the list (passive badge)
 *     'unknown'     — not on the list, and not dismissed this session (ask banner)
 *     'dismissed'   — not on the list, but "Not now" was clicked (render nothing)
 *     'cannot-tell' — the server could not tell which network the call came from (one quiet
 *                     line, no button). ⚠️ Not "nothing": if this ever happened to everyone,
 *                     nothing shown would be a feature that had stopped working without a
 *                     sign. And not the ask: a Yes could only be refused.
 */
export function useInternalIpStatus() {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  // 'unanswered' (nothing is shown) · true · false · 'cannot-tell' (the server answered null)
  const [known, setKnown] = useState('unanswered');
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; }
  });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const res = await httpsCallable(functions, 'myNetworkStatus')();
        if (cancelled) return;
        // A plain yes or no is an answer. EXACTLY null is the server's "I cannot tell which
        // network this call came from". Anything else shows nothing: an ask the page cannot
        // stand behind is worse than none, and so is a badge.
        const answer = res.data ? res.data.known : undefined;
        if (typeof answer === 'boolean') setKnown(answer);
        else if (answer === null) setKnown('cannot-tell');
      } catch (err) {
        console.warn('[useInternalIpStatus] check failed:', err.message);
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISSED_KEY, '1'); } catch { /* not remembered, that is all */ }
  }, []);

  const handleAdd = useCallback(async () => {
    setAdding(true);
    try {
      // Sent nothing: the server adds the network this call came from, and no other.
      const res = await httpsCallable(functions, 'addMyNetwork')();
      if (res.data?.known !== true) throw new Error('The network was not added.');
      setKnown(true);
    } catch (err) {
      // ⚠️ Said, not swallowed. This used to be a line in the console: a colleague clicked Yes,
      // nothing happened, and the page went on asking.
      console.error('[useInternalIpStatus] add failed:', err.message);
      toast.error(err.message || 'Your network could not be added', {
        debugInfo: {
          operation: 'addMyNetwork',
          error: err.stack || String(err),
          context: 'Engagement → Exclude my visits → Yes',
        },
      });
      // The server could not tell which network the call came from: asking again cannot work,
      // and the message has said why. Any other failure may pass, so the ask stays.
      if (err.code === 'functions/failed-precondition') dismiss();
    } finally {
      setAdding(false);
    }
  }, [toast, dismiss]);

  let status = 'none';
  if (isAuthenticated && known !== 'unanswered') {
    if (known === 'cannot-tell') status = 'cannot-tell'; // said whatever "Not now" was clicked for
    else if (known) status = 'known';
    else if (dismissed) status = 'dismissed';
    else status = 'unknown';
  }

  return { status, adding, handleAdd, handleDismiss: dismiss };
}

/**
 * InternalIpBadge — passive reassurance shown once the current network is
 * already on the allowlist. Deliberately much quieter than the ask banner;
 * sits inline under the page header's action buttons.
 */
export const InternalIpBadge = () => (
  <div className="internal-ip-badge" title="Visits from this network are excluded from Engagement views">
    <FaUserSecret aria-hidden="true" />
    Your visits from this network are excluded
  </div>
);

/**
 * InternalIpCannotTell — the server could not tell which network this colleague is on, so it
 * can neither say their visits are left out nor offer to leave them out. Said in the badge's
 * place, as quietly, with no button: there is nothing here they can press that would work.
 */
export const InternalIpCannotTell = () => (
  <div className="internal-ip-badge" role="status">
    <FaUserSecret aria-hidden="true" />
    This network could not be identified, so your visits are not being left out
  </div>
);

/**
 * InternalIpAskBanner — shown when the current network isn't recognised yet.
 * Full-width, sits at the top of the page content (not tucked under the
 * header) since it's asking for a decision, not just reporting status.
 */
export const InternalIpAskBanner = ({ adding, onAdd, onDismiss }) => (
  <div className="internal-ip-banner internal-ip-banner--top">
    <span className="internal-ip-banner__text">
      <strong>Exclude my visits</strong> so your own testing doesn’t count as customer activity here?
    </span>
    <div className="internal-ip-banner__actions">
      <Button variant="primary" size="small" onClick={onAdd} loading={adding}>Yes</Button>
      <Button variant="ghost" size="small" onClick={onDismiss}>Not now</Button>
    </div>
  </div>
);
